/**
 * Horarios y vacaciones (US2): la semana de todos, y las tarjetas en columnas.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { api } from '../../lib/api'
import {
    aplicarOrden,
    bajarEnOrden,
    leerOrdenGuardado,
    moverAHueco,
    recordarOrden,
    subirEnOrden,
} from '../../lib/horarios-orden'
import {
    claseGridTarjetas,
    leerColumnasTarjetas,
    leerOcultarCerrados,
    leerOcultos,
    recordarColumnasTarjetas,
    recordarOcultarCerrados,
    recordarOcultos,
    type ColumnasTarjetas,
} from '../../lib/horarios-vista'
import { hoyISO } from '../calendario-fechas'
import { diaSemanaDe, semanaDe } from '../calendario-geometria'
import { copiarDiaAlResto, hayRestoAbierto, type Plan } from '../../lib/horarios-tramos'
import { VistaCoberturaSemanal, type SeleccionHorario } from './horarios-cobertura'
import { PreguntaAlcanceHorario } from './horarios-pregunta'
import { TarjetaHorario, type Vacacion } from './horarios-tarjeta'

const COLUMNAS: { v: ColumnasTarjetas; etiqueta: string }[] = [
    { v: 'auto', etiqueta: 'Auto' },
    { v: '2', etiqueta: '2' },
    { v: '3', etiqueta: '3' },
    { v: '4', etiqueta: '4' },
]

export function HorariosNegocio({ embebido = false }: { embebido?: boolean }) {
    const { accountId } = useParams<{ accountId: string }>()
    const [colocando, setColocando] = useState(false)
    const [orden, setOrden] = useState<number[] | null>(null)
    const [arrastrando, setArrastrando] = useState<number | null>(null)
    const [sobreHueco, setSobreHueco] = useState<number | null>(null)
    const [ocultarCerrados, setOcultarCerrados] = useState(leerOcultarCerrados)
    const [ocultos, setOcultos] = useState<ReadonlySet<number>>(
        () => new Set(accountId ? leerOcultos(accountId) : []),
    )
    const [columnas, setColumnas] = useState<ColumnasTarjetas>(leerColumnasTarjetas)
    const [planes, setPlanes] = useState<Record<number, Plan>>({})
    const [sucios, setSucios] = useState<ReadonlySet<number>>(new Set())
    const [seleccionado, setSeleccionado] = useState<SeleccionHorario>(null)
    const [pregunta, setPregunta] = useState<{
        profesionalId: number
        dia: keyof Plan
        x: number
        y: number
    } | null>(null)

    const horarios = useQuery({
        queryKey: ['horarios', accountId],
        queryFn: () =>
            api.get<{ profesionales: { id: number; nombre: string; plan: Plan; color: string }[] }>(
                `/business/${accountId}/agenda/horarios`,
            ),
        enabled: Boolean(accountId),
    })

    const vacaciones = useQuery({
        queryKey: ['vacaciones', accountId],
        queryFn: () => api.get<{ vacaciones: Vacacion[] }>(`/business/${accountId}/agenda/vacaciones`),
        enabled: Boolean(accountId),
    })

    const idsServidor = useMemo(
        () => horarios.data?.profesionales.map((p) => p.id) ?? [],
        [horarios.data],
    )

    const ids = useMemo(() => {
        if (idsServidor.length === 0) return []
        const base = orden ?? (accountId ? leerOrdenGuardado(accountId) : null)
        return aplicarOrden(idsServidor, base)
    }, [accountId, idsServidor, orden])

    useEffect(() => {
        const lista = horarios.data?.profesionales
        if (!lista) return
        setPlanes((prev) => {
            const next = { ...prev }
            for (const p of lista) {
                if (!sucios.has(p.id)) next[p.id] = structuredClone(p.plan)
            }
            return next
        })
    }, [horarios.data, sucios])

    const porId = useMemo(() => {
        const m = new Map<number, { id: number; nombre: string; plan: Plan; color: string }>()
        for (const p of horarios.data?.profesionales ?? []) {
            m.set(p.id, { ...p, plan: planes[p.id] ?? p.plan })
        }
        return m
    }, [horarios.data, planes])

    const apilados = ids.map((id) => porId.get(id)).filter((p): p is NonNullable<typeof p> => Boolean(p))
    const enPantalla = apilados.filter((p) => !ocultos.has(p.id))
    const dias = semanaDe(hoyISO())
    const vacs = vacaciones.data?.vacaciones ?? []

    useEffect(() => {
        setOcultos(new Set(accountId ? leerOcultos(accountId) : []))
    }, [accountId])

    function fijar(siguiente: number[]) {
        setOrden(siguiente)
        if (accountId) recordarOrden(accountId, siguiente)
    }

    function elegirColumnas(v: ColumnasTarjetas) {
        setColumnas(v)
        recordarColumnasTarjetas(v)
    }

    function elegirOcultar(v: boolean) {
        setOcultarCerrados(v)
        recordarOcultarCerrados(v)
    }

    function toggleVisible(id: number) {
        setOcultos((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            if (accountId) recordarOcultos(accountId, [...next])
            return next
        })
        setSeleccionado((s) => (s?.profesionalId === id ? null : s))
    }

    function cambiarPlan(id: number, plan: Plan) {
        setPlanes((prev) => ({ ...prev, [id]: plan }))
        setSucios((prev) => new Set(prev).add(id))
    }

    function pedirAlcance(profesionalId: number, dia: keyof Plan, punto: { x: number; y: number }) {
        const plan = planes[profesionalId]
        if (!plan || !hayRestoAbierto(plan, dia)) return
        setPregunta({ profesionalId, dia, x: punto.x, y: punto.y })
    }

    return (
        <div className="space-y-3">
            <header className="flex flex-wrap items-baseline justify-between gap-2">
                {!embebido && (
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-100">Horarios y vacaciones</h2>
                        <p className="text-xs text-neutral-500">
                            Arriba la semana de todos. Abajo, cada uno en su tarjeta.
                        </p>
                    </div>
                )}
                {embebido && <h3 className="text-sm font-medium text-neutral-200">Horarios</h3>}
                <div className="flex flex-wrap items-center gap-2">
                    {apilados.length > 1 && (
                        <button
                            type="button"
                            onClick={() => setColocando((v) => !v)}
                            className={
                                'min-h-11 rounded-md border px-2 py-0.5 text-[11px] md:min-h-0 ' +
                                (colocando
                                    ? 'border-sky-500/50 bg-sky-500/15 text-sky-200'
                                    : 'border-neutral-800 text-neutral-400 hover:text-neutral-200')
                            }
                        >
                            {colocando ? 'Listo' : 'Colocar'}
                        </button>
                    )}
                </div>
            </header>

            {horarios.isPending && <p className="text-sm text-neutral-500">Cargando…</p>}
            {horarios.isError && <p className="text-sm text-rose-400">No se pudieron leer los horarios.</p>}

            {apilados.length > 0 && (
                <VistaCoberturaSemanal
                    profesionales={apilados}
                    enPantalla={enPantalla}
                    vacaciones={vacs}
                    dias={dias}
                    ocultarCerrados={ocultarCerrados}
                    onOcultarCerrados={elegirOcultar}
                    onToggleVisible={toggleVisible}
                    seleccionado={seleccionado}
                    onSeleccionar={setSeleccionado}
                    onCambiarPlan={cambiarPlan}
                    onPreguntarAlcance={pedirAlcance}
                />
            )}

            {enPantalla.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-neutral-500">
                        {colocando
                            ? 'Arrastra una tarjeta a otro hueco. El orden es de esta pantalla.'
                            : 'Las tarjetas'}
                    </p>
                    <div className="flex items-center gap-1" role="group" aria-label="Tarjetas por fila">
                        {COLUMNAS.map((c) => (
                            <button
                                key={c.v}
                                type="button"
                                onClick={() => elegirColumnas(c.v)}
                                className={
                                    'min-h-11 min-w-11 rounded px-2 py-0.5 text-[11px] md:min-h-0 md:min-w-0 ' +
                                    (columnas === c.v
                                        ? 'bg-neutral-800 text-neutral-100'
                                        : 'text-neutral-500 hover:text-neutral-300')
                                }
                            >
                                {c.etiqueta}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className={claseGridTarjetas(columnas)}>
                {enPantalla.map((p, i) => (
                    <div
                        key={p.id}
                        draggable={colocando}
                        onDragStart={() => setArrastrando(p.id)}
                        onDragEnd={() => {
                            setArrastrando(null)
                            setSobreHueco(null)
                        }}
                        onDragOver={(e) => {
                            if (!colocando) return
                            e.preventDefault()
                            setSobreHueco(i)
                        }}
                        onDrop={(e) => {
                            e.preventDefault()
                            if (arrastrando === null) return
                            fijar(moverAHueco(ids, arrastrando, i))
                            setArrastrando(null)
                            setSobreHueco(null)
                        }}
                        className={
                            (colocando ? 'cursor-grab ' : '') +
                            (sobreHueco === i && arrastrando !== p.id
                                ? 'ring-2 ring-sky-500/60 ring-offset-2 ring-offset-neutral-950 '
                                : '') +
                            (arrastrando === p.id ? 'opacity-60 ' : '')
                        }
                    >
                        <TarjetaHorario
                            accountId={accountId ?? ''}
                            profesional={p}
                            plan={p.plan}
                            onCambiarPlan={(plan) => cambiarPlan(p.id, plan)}
                            vacaciones={vacs.filter((v) => v.profesionalId === p.id)}
                            colocando={colocando}
                            hueco={i + 1}
                            totalHuecos={enPantalla.length}
                            seleccionada={seleccionado?.profesionalId === p.id}
                            diaSeleccionado={
                                seleccionado?.profesionalId === p.id
                                    ? diaSemanaDe(seleccionado.dia)
                                    : null
                            }
                            onSeleccionar={() =>
                                setSeleccionado({ profesionalId: p.id, dia: dias[0]!, tramo: 0 })
                            }
                            onPreguntarAlcance={(dia, punto) => pedirAlcance(p.id, dia, punto)}
                            onGuardado={() =>
                                setSucios((prev) => {
                                    const next = new Set(prev)
                                    next.delete(p.id)
                                    return next
                                })
                            }
                            onSubir={() => fijar(subirEnOrden(ids, p.id))}
                            onBajar={() => fijar(bajarEnOrden(ids, p.id))}
                        />
                    </div>
                ))}
            </div>

            {pregunta && (
                <PreguntaAlcanceHorario
                    x={pregunta.x}
                    y={pregunta.y}
                    alSoloEste={() => setPregunta(null)}
                    alResto={() => {
                        const plan = planes[pregunta.profesionalId]
                        if (plan) cambiarPlan(pregunta.profesionalId, copiarDiaAlResto(plan, pregunta.dia))
                        setPregunta(null)
                    }}
                    alCerrar={() => setPregunta(null)}
                />
            )}

            {horarios.isSuccess && apilados.length === 0 && (
                <p className="rounded-lg border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-400">
                    Este negocio aún no tiene profesionales en la agenda.
                </p>
            )}
        </div>
    )
}
